import React, { memo } from 'react';
import { PageBanner } from '../components/PageBanner';
import { ContactCTA } from '../components/ContactCTA';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { HomeIcon, ShieldCheckIcon, ZapIcon, CheckCircleIcon, BriefcaseIcon, SettingsIcon } from 'lucide-react';
export function OccupationalTherapyPage() {
  const {
    ref: introRef,
    isVisible: isIntroVisible
  } = useScrollAnimation();
  const {
    ref: benefitsRef,
    isVisible: isBenefitsVisible
  } = useScrollAnimation();
  const {
    ref: detailsRef,
    isVisible: isDetailsVisible
  } = useScrollAnimation();
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useScrollAnimation();
  return <main data-id="element-935">
      <PageBanner title="Occupational Therapy" subtitle="Enhancing independence through functional adaptation and skill development." breadcrumbs={[{
      label: 'Services',
      path: '/services'
    }, {
      label: 'Occupational Therapy',
      path: '/services/occupational-therapy'
    }]} data-id="element-936" />

      {/* Intro Section */}
      <section className="py-16 md:py-20 bg-gradient-to-tr from-light-alt via-light to-light-alt" data-id="element-937">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-938">
          <div ref={introRef} className={`transition-all duration-1000 ease-out ${isIntroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-939">
            <div className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6" data-id="element-940">
              <BriefcaseIcon className="h-4 w-4 text-primary" data-id="element-941" />
              <span className="text-primary text-sm font-semibold" data-id="element-942">
                Functional Rehabilitation
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-6 leading-tight" data-id="element-943">
              Regain Independence in Your Daily Activities
            </h2>
            <div className="space-y-5 text-dark-muted text-lg leading-relaxed" data-id="element-944">
              <p data-id="element-945">
                Our occupational therapy services focus on helping patients
                regain the skills needed for daily living and working. We
                specialize in adapting environments and tasks to fit the
                individual, ensuring you can perform your "occupations" — the
                activities that are meaningful to you — with as much
                independence as possible.
              </p>
              <p data-id="element-946">
                Whether you are recovering from a stroke, managing a chronic
                condition, or adapting to age-related changes, our therapists
                provide the tools and strategies you need to thrive at home.
              </p>
            </div>

            {/* RN Badge */}
            <div className="mt-8 flex items-center bg-primary/5 border border-primary/10 rounded-xl p-5" data-id="element-947">
              <ShieldCheckIcon className="h-10 w-10 text-primary mr-4 flex-shrink-0" data-id="element-948" />
              <div data-id="element-949">
                <h4 className="font-bold text-dark" data-id="element-950">Licensed Therapists</h4>
                <p className="text-sm text-dark-muted" data-id="element-951">
                  Our occupational therapy programs are conducted by licensed
                  OTs and supervised by our clinical leadership.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-light via-light-alt to-light" data-id="element-952">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-953">
          <div ref={benefitsRef} className={`transition-all duration-1000 ease-out ${isBenefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-954">
            <div className="text-center mb-14" data-id="element-955">
              <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4" data-id="element-956">
                Benefits of Occupational Therapy
              </h2>
              <div className="w-16 h-1.5 bg-secondary mx-auto mb-6 rounded-full" data-id="element-957"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-958">
              {[{
              icon: HomeIcon,
              title: 'Home Adaptation',
              desc: 'Modifying your living space to improve safety and ease of use.'
            }, {
              icon: SettingsIcon,
              title: 'Adaptive Equipment',
              desc: 'Training on specialized tools to assist with dressing, eating, and more.'
            }, {
              icon: ZapIcon,
              title: 'Energy Conservation',
              desc: 'Strategies to manage fatigue and complete tasks more efficiently.'
            }, {
              icon: ShieldCheckIcon,
              title: 'Cognitive Support',
              desc: 'Exercises and tools to help with memory, focus, and organization.'
            }].map((benefit, idx) => <div key={idx} className="bg-light-alt rounded-2xl p-7 shadow-card hover:shadow-card-hover border border-gray-200 transition-all duration-300 transform hover:-translate-y-1 group" data-id="element-959">
                  <div className="bg-primary/10 w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary transition-colors duration-300" data-id="element-960">
                    <benefit.icon className="h-7 w-7 text-primary group-hover:text-white transition-colors duration-300" data-id="element-961" />
                  </div>
                  <h3 className="text-lg font-bold text-dark mb-3" data-id="element-962">
                    {benefit.title}
                  </h3>
                  <p className="text-dark-muted text-sm leading-relaxed" data-id="element-963">
                    {benefit.desc}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Details Section */}
      <section className="py-16 md:py-20 bg-gradient-to-tr from-light-alt via-light to-light-alt" data-id="element-964">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-965">
          <div ref={detailsRef} className={`transition-all duration-1000 ease-out ${isDetailsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} data-id="element-966">
            <h2 className="text-3xl font-bold text-dark mb-6" data-id="element-967">
              Our Occupational Therapy Services Include
            </h2>
            <p className="text-dark-muted text-lg leading-relaxed mb-8" data-id="element-968">
              We provide a holistic approach to rehabilitation, focusing on the
              physical, cognitive, and environmental factors that affect your
              independence.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-969">
              {['Activities of Daily Living (ADL) training', 'Adaptive equipment recommendations', 'Home safety and accessibility audits', 'Upper extremity rehabilitation', 'Cognitive rehabilitation therapy', 'Energy conservation techniques', 'Fine motor skill development', 'Caregiver education and training'].map((item, idx) => <div key={idx} className="flex items-center p-4 bg-light rounded-xl border border-gray-200" data-id="element-970">
                  <CheckCircleIcon className="h-6 w-6 text-secondary mr-3 flex-shrink-0" data-id="element-971" />
                  <span className="text-dark font-medium" data-id="element-972">{item}</span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 bg-light" data-id="element-973">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-974">
          <div ref={ctaRef} className={`bg-primary rounded-3xl p-10 md:p-14 text-center shadow-xl relative overflow-hidden transition-all duration-1000 ease-out ${isCtaVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-95'}`} data-id="element-975">
            <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(#ffffff 2px, transparent 2px)',
            backgroundSize: '30px 30px'
          }} data-id="element-976"></div>
            <div className="relative z-10" data-id="element-977">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{
              fontFamily: "'Cinzel', serif"
            }} data-id="element-978">
                Ready to Enhance Your Independence?
              </h2>
              <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto" data-id="element-979">
                Contact us today to discuss your occupational therapy needs and
                schedule an initial assessment with our licensed therapists.
              </p>
              <a href="#/contact" className="inline-block bg-white text-primary-dark hover:bg-light-alt px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1" data-id="element-980">
                Contact Us Today
              </a>
            </div>
          </div>
        </div>
      </section>

      <ContactCTA data-id="element-981" />
    </main>;
}